import { useEffect, useRef, useState } from 'react'
import { useAuth } from './AuthProvider'

const AxiosInterceptor = ({ children }) => {
  const { user, logout, axiosInstance } = useAuth()
  const [interceptorsSet, setInterceptorsSet] = useState(false)

  // Keep refs always current as of the latest render (synchronous, not in an effect).
  // This ensures any in-flight interceptor callbacks read the latest user/logout
  // even when the effect hasn't re-run yet — fixing the race on first login where
  // child components fire API calls before the effect's closure has been updated.
  const userRef = useRef(user)
  const logoutRef = useRef(logout)
  userRef.current = user
  logoutRef.current = logout

  useEffect(() => {
    const reqUseInterceptor = (config) => {
      const currentUser = userRef.current
      if (!currentUser) {
        return config
      }

      const token = currentUser.access_token
      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
      } else {
        delete config.headers['Authorization']
      }
      return config
    }

    const reqUseErrInterceptor = (error) => {
      return Promise.reject(error)
    }

    const intercept1 = axiosInstance.interceptors.request.use(
      reqUseInterceptor,
      reqUseErrInterceptor,
    )

    const resUseInterceptor = (response) => response
    const resUseErrInterceptor = (error) => {
      if (error.response && error.response.status === 401) {
        logoutRef.current()
      }
      return Promise.reject(error)
    }

    const intercept2 = axiosInstance.interceptors.response.use(
      resUseInterceptor,
      resUseErrInterceptor,
    )

    setInterceptorsSet(true)

    return () => {
      axiosInstance.interceptors.request.eject(intercept1)
      axiosInstance.interceptors.response.eject(intercept2)
    }
  }, []) // register interceptors once; refs keep them up-to-date

  return interceptorsSet && children
}

export default AxiosInterceptor
